// ./src/hooks/useFormFieldTypes.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  FormFieldType,
  FormFieldTypesService,
  FormFieldTypeFilter,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseFormFieldTypesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<FormFieldTypeFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseFormFieldTypesReturn {
  // Data state
  formFieldType: FormFieldType | null;
  formFieldTypes: FormFieldType[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<FormFieldTypeFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<FormFieldTypeFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<FormFieldType[]>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<FormFieldTypeFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "id", direction: "asc" }];

export const useFormFieldTypes = (
  options: UseFormFieldTypesOptions = {}
): UseFormFieldTypesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [formFieldType, setFormFieldType] = useState<FormFieldType | null>(
    null
  );
  const [formFieldTypes, setFormFieldTypes] = useState<FormFieldType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<FormFieldTypeFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => formFieldTypes.length === 0 && !loading,
    [formFieldTypes.length, loading]
  );

  // Helper function to update form field type in list if it exists, or add it if not
  const updateOrAddFormFieldTypeInList = useCallback(
    (updatedFormFieldType: FormFieldType) => {
      setFormFieldTypes((prev) => {
        const existingIndex = prev.findIndex(
          (ft) => ft.id === updatedFormFieldType.id
        );
        if (existingIndex >= 0) {
          const newFormFieldTypes = [...prev];
          newFormFieldTypes[existingIndex] = updatedFormFieldType;
          return newFormFieldTypes;
        } else {
          return [updatedFormFieldType, ...prev];
        }
      });
    },
    []
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setFormFieldType(null);
    setFormFieldTypes([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
  }, []);

  // Get single form field type (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await FormFieldTypesService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const formFieldTypeData = result.data[0] as FormFieldType;
          setFormFieldType(formFieldTypeData);
          updateOrAddFormFieldTypeInList(formFieldTypeData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load form field type";
          setLastError(errorMessage);
          setFormFieldType(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load form field type";
        setLastError(errorMessage);
        setFormFieldType(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFormFieldTypeInList]
  );

  // List form field types (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<FormFieldTypeFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setFormFieldTypes([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await FormFieldTypesService.list(
          filter as FormFieldTypeFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newFormFieldTypes = Array.isArray(result.data)
            ? result.data
            : [];

          if (isAppending) {
            setFormFieldTypes((prev) => {
              const existingIds = new Set(prev.map((ft) => ft.id));
              const uniqueNewFormFieldTypes = newFormFieldTypes.filter(
                (ft: any) => !existingIds.has(ft.id)
              );
              return [...prev, ...uniqueNewFormFieldTypes];
            });
          } else {
            setFormFieldTypes(newFormFieldTypes);
          }

          setHasMore(newFormFieldTypes.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load form field types";
          setLastError(errorMessage);
          if (!isAppending) setFormFieldTypes([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load form field types";
        setLastError(errorMessage);
        if (!isAppending) setFormFieldTypes([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many form field types by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<FormFieldType[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await FormFieldTypesService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedFormFieldTypes = Array.isArray(result.data)
            ? result.data
            : [];
          loadedFormFieldTypes.forEach((formFieldType: any) => {
            updateOrAddFormFieldTypeInList(formFieldType);
          });
          setLastError(null);
          return loadedFormFieldTypes;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load form field types";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load form field types";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFormFieldTypeInList]
  );

  // Load more form field types
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh form field types
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    formFieldType,
    formFieldTypes,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    get,
    list,
    getMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    reset,
    lastError,
    clearError,
  };
};
